import { isAnyOf, type Middleware } from "@reduxjs/toolkit";
import { Cookies } from "react-cookie";
import i18n from "../../translations/i18";
import globalReducer, {
  toAddOrRemoveProduct, toAddOrRemoveService, toRemoveProduct, toRemoveService,
  toUpdateQuantity, setProducts, setServices, setVehicle, setAppointment, setSelectedBranch,
} from "../slices/globalSlice";
import { requestCartSync, toGetMyCart, toSaveMyCart, toDeleteMyCart } from "../actions/cart.actions";
import { buildSavedCartPayload } from "../../utils/cartPersistence";
import { cancelCartAddedFeedback, publishCartAddedResult, signalCartAddedFeedback } from "../../utils/cartAddedFeedback";
import { showNotitfication } from "../../utils/Utils";

const isMutation = isAnyOf(
  toAddOrRemoveProduct, toAddOrRemoveService, toRemoveProduct, toRemoveService,
  toUpdateQuantity, setProducts, setServices, setVehicle, setAppointment, setSelectedBranch,
);

/** Serialize all cart changes and display only the canonical server response. */
const cartAdditionMiddleware: Middleware<{}, { global: ReturnType<typeof globalReducer> }> = api => {
  let queue: Promise<unknown> = Promise.resolve();
  const dispatch = api.dispatch as any;
  return next => action => {
    const refresh = requestCartSync.match(action);
    if (!refresh && !isMutation(action)) return next(action);
    const session = new Cookies().get("apiKey");
    const product = toAddOrRemoveProduct.match(action);
    const addition = product || toAddOrRemoveService.match(action);
    const item = addition ? { id: action.payload.id, type: product ? "product" as const : "service" as const } : undefined;
    // The reducer drops a service add with no car selected; say why instead of failing silently.
    if (addition && !product && !api.getState().global.vehicle?.id) {
      cancelCartAddedFeedback();
      publishCartAddedResult(false, item);
      showNotitfication(i18n.t("services.chooseVehicle.required"), "error");
      return Promise.resolve();
    }
    if (!session) {
      if (item) { cancelCartAddedFeedback(); publishCartAddedResult(false, item); }
      return refresh || addition ? Promise.resolve() : next(action);
    }

    const update = async () => {
      const sameSession = () => new Cookies().get("apiKey") === session;
      if (!sameSession()) {
        if (item) { cancelCartAddedFeedback(); publishCartAddedResult(false, item); }
        return;
      }
      // Read first so stale storage or another tab cannot become the update's baseline.
      const latest = await dispatch(toGetMyCart(i18n.language || "en"));
      if (!toGetMyCart.fulfilled.match(latest) || !sameSession() || !api.getState().global.cartReady) {
        if (item) { cancelCartAddedFeedback(); publishCartAddedResult(false, item); }
        return latest;
      }
      if (refresh) return latest;
      const current = api.getState().global;
      const candidate = globalReducer(current, action);
      if (candidate === current) return latest;
      const cart = buildSavedCartPayload(candidate);
      // A selected vehicle/branch before any items is UI draft state, not a cart write.
      if (!cart) return next(action);
      if (addition) signalCartAddedFeedback();
      const hasItems = cart.products.length > 0 || cart.services.length > 0;
      const result = await dispatch(hasItems ? toSaveMyCart({ ...cart, feedbackItem: item,
        presentation: { products: candidate.products, services: candidate.services,
          vehicle: candidate.vehicle, appointment: candidate.appointment },
      }) : toDeleteMyCart());
      // No replay of the local mutation: reducers apply the server's actual contents.
      if ((toSaveMyCart.rejected.match(result) || toDeleteMyCart.rejected.match(result)) && sameSession()) {
        await dispatch(toGetMyCart(i18n.language || "en"));
      }
      return result;
    };
    queue = queue.then(update, update);
    return queue;
  };
};

export default cartAdditionMiddleware;
